import React from 'react';
import type { RestaurateurView } from '../../shared/types';
import AtableLogo from '../../shared/design/components/AtableLogo';
import { useFocusTrap } from '../../shared/design/useFocusTrap';

interface SidebarProps {
    currentView: RestaurateurView;
    setCurrentView: (view: RestaurateurView) => void;
    /** Tiroir ouvert (mobile / tablette). Sur desktop la barre est toujours visible. */
    open?: boolean;
    onClose?: () => void;
    /** Nombre de demandes de récompense en attente (pastille). */
    pendingRedemptions?: number;
    /** Nombre de réservations à confirmer (pastille). */
    pendingReservations?: number;
}

type Icon = React.FC<{ className?: string }>;

const path = (d: string): Icon => ({ className }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d={d} />
    </svg>
);

const I = {
    home: path('M3 11.5 12 4l9 7.5M5 10v10h5v-6h4v6h5V10'),
    calendar: path('M8 3v3m8-3v3M4 9h16M6 5h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2z'),
    tap: path('M12 8a4 4 0 0 1 4 4M12 4a8 8 0 0 1 8 8M9.5 13.5 12 20l1.5-3.5L17 15l-6.5-2.5a.7.7 0 0 0-1 1z'),
    gift: path('M20 12v9H4v-9M2 7h20v5H2zM12 22V7m0 0H7.5a2.5 2.5 0 1 1 0-5C11 2 12 7 12 7zm0 0h4.5a2.5 2.5 0 1 0 0-5C13 2 12 7 12 7z'),
    heart: path('M12 20.5s-7.5-4.6-7.5-10A4.3 4.3 0 0 1 12 7.6a4.3 4.3 0 0 1 7.5 2.9c0 5.4-7.5 10-7.5 10z'),
    megaphone: path('M3 11v2a1 1 0 0 0 1 1h2l5 4V6L6 10H4a1 1 0 0 0-1 1zm15-3a5 5 0 0 1 0 8M15 9.5a2.5 2.5 0 0 1 0 5'),
    users: path('M16 19v-1a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v1m18 0v-1a4 4 0 0 0-3-3.87M13 3.13a4 4 0 0 1 0 7.75M13 7a4 4 0 1 1-8 0 4 4 0 0 1 8 0z'),
    chart: path('M4 20V10m6 10V4m6 16v-7m4 7H2'),
    store: path('M3 9l1.5-5h15L21 9M3 9a3 3 0 0 0 6 0 3 3 0 0 0 6 0 3 3 0 0 0 6 0M5 11v9h14v-9M10 20v-5h4v5'),
    team: path('M17 20h5v-1a3 3 0 0 0-4.5-2.6M17 20H7m10 0v-1a4 4 0 0 0-.4-1.6M7 20H2v-1a3 3 0 0 1 4.5-2.6M7 20v-1a4 4 0 0 1 .4-1.6m0 0a5 5 0 0 1 9.2 0M15 7a3 3 0 1 1-6 0 3 3 0 0 1 6 0zm6 3a2 2 0 1 1-4 0 2 2 0 0 1 4 0zM7 10a2 2 0 1 1-4 0 2 2 0 0 1 4 0z'),
    pos: path('M4 4h16v6H4zM4 14h4v4H4zm6 0h4v4h-4zm6 0h4v4h-4z'),
    nfc: path('M8.1 16.4a5.5 5.5 0 0 1 7.8 0M12 20h.01M4.9 12.9a10 10 0 0 1 14.2 0M1.4 9.4a15 15 0 0 1 21.2 0'),
};

interface NavItem { id: RestaurateurView; label: string; icon: Icon; badge?: number }
interface NavGroup { label: string; items: NavItem[] }

const Sidebar: React.FC<SidebarProps> = ({ currentView, setCurrentView, open = false, onClose, pendingRedemptions, pendingReservations }) => {
    const groups: NavGroup[] = [
        {
            label: 'Au quotidien',
            items: [
                { id: 'reservations', label: 'Réservations', icon: I.calendar, badge: pendingReservations },
                { id: 'nfc-pos', label: 'Encaissement fidélité', icon: I.tap },
                { id: 'redemptions', label: 'Récompenses à valider', icon: I.gift, badge: pendingRedemptions },
            ],
        },
        {
            label: 'Fidéliser',
            items: [
                { id: 'loyalty-program', label: 'Programme de fidélité', icon: I.heart },
                { id: 'campaigns', label: 'Campagnes & promos', icon: I.megaphone },
                { id: 'customers', label: 'Clients', icon: I.users },
                { id: 'analytics', label: 'Analyses', icon: I.chart },
            ],
        },
        {
            label: 'Établissement',
            items: [
                { id: 'profile-editor', label: 'Mon établissement', icon: I.store },
                { id: 'staff', label: 'Équipe', icon: I.team },
                { id: 'nfc-tags', label: 'Puces NFC', icon: I.nfc },
            ],
        },
    ];

    const drawerRef = useFocusTrap<HTMLDivElement>(open, onClose);

    const go = (view: RestaurateurView) => {
        setCurrentView(view);
        onClose?.();
    };

    const isActive = (id: RestaurateurView) =>
        currentView === id || (id === 'customers' && currentView === 'customerProfile');

    const content = (
        <div className="flex h-full flex-col">
            {/* Logo */}
            <div className="flex items-center justify-between h-16 px-5">
                <button onClick={() => go('reservations')} className="flex items-center rounded-lg" aria-label="Accueil">
                    <AtableLogo variant="wordmark" height={34} />
                </button>
                {onClose && (
                    <button
                        onClick={onClose}
                        className="lg:hidden w-9 h-9 rounded-full flex items-center justify-center text-gray-600 hover:bg-white/70"
                        aria-label="Fermer le menu"
                    >
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" d="M6 6l12 12M6 18L18 6" /></svg>
                    </button>
                )}
            </div>

            {/* Navigation */}
            <nav aria-label="Navigation principale" className="flex-1 overflow-y-auto scroll-thin px-3 pb-4 pt-1 space-y-5">
                {groups.map((group) => (
                    <div key={group.label}>
                        <p className="mb-1.5 px-3 text-xs font-medium text-gray-500">{group.label}</p>
                        <ul className="space-y-0.5">
                            {group.items.map((item) => {
                                const active = isActive(item.id);
                                return (
                                    <li key={item.id}>
                                        <button
                                            onClick={() => go(item.id)}
                                            aria-current={active ? 'page' : undefined}
                                            className={[
                                                'group flex w-full items-center gap-2.5 h-9 px-3 rounded-lg text-sm leading-none transition-colors duration-150',
                                                active
                                                    ? 'bg-brand-50 text-brand-700 font-medium'
                                                    : 'text-gray-700 font-normal hover:bg-black/[0.04] hover:text-gray-900',
                                            ].join(' ')}
                                        >
                                            <item.icon className={`w-[18px] h-[18px] flex-shrink-0 ${active ? 'text-brand-600' : 'text-gray-500 group-hover:text-gray-700'}`} />
                                            <span className="flex-1 text-left truncate">{item.label}</span>
                                            {item.badge ? (
                                                <span className="min-w-[20px] h-5 px-1.5 inline-flex items-center justify-center rounded-md bg-brand-500 text-cream text-xs font-semibold leading-none">
                                                    {item.badge > 99 ? '99+' : item.badge}
                                                </span>
                                            ) : null}
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                ))}
            </nav>

            {/* Rémi + caisse */}
            <div className="px-3 pb-4 space-y-2">
                <button
                    onClick={() => go('remi-expert')}
                    className={[
                        'group relative w-full overflow-hidden rounded-xl p-3 pr-20 text-left transition-colors duration-150',
                        currentView === 'remi-expert'
                            ? 'bg-brand-500 text-cream'
                            : 'bg-peach-100 text-gray-900 hover:bg-peach-200',
                    ].join(' ')}
                >
                    <p className="text-sm font-medium leading-snug">Rémi</p>
                    <p className={`mt-0.5 text-xs ${currentView === 'remi-expert' ? 'text-cream/80' : 'text-gray-600'}`}>Conseils & idées pour votre fidélité</p>
                    <img
                        src="/brand/remi.png"
                        alt=""
                        className="pointer-events-none absolute -right-2 -bottom-2 w-[84px] h-auto drop-shadow-md transition-transform duration-300 group-hover:-translate-y-1 group-hover:-rotate-3"
                        draggable={false}
                    />
                </button>

                <button
                    onClick={() => go('pos')}
                    className="flex w-full items-center gap-2.5 h-9 px-3 rounded-lg text-sm leading-none text-gray-700 hover:bg-black/[0.04] hover:text-gray-900 transition-colors"
                >
                    <I.pos className="w-[18px] h-[18px] text-gray-500" />
                    <span className="flex-1 text-left">Ouvrir la caisse</span>
                    <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M14 5h5v5M19 5l-8 8M10 6H6a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-4" /></svg>
                </button>
            </div>
        </div>
    );

    return (
        <>
            {/* Desktop : barre fixe */}
            <aside className="hidden lg:flex w-[248px] flex-shrink-0 flex-col bg-cream dark:bg-gray-950 border-r border-black/[0.08] dark:border-gray-800">
                {content}
            </aside>

            {/* Mobile / tablette : tiroir modal (focus piégé, Échap, focus rendu au bouton menu) */}
            {open && (
                <div className="lg:hidden fixed inset-0 z-50">
                    <div className="absolute inset-0 bg-black/40" onClick={onClose} aria-hidden="true" />
                    <div
                        ref={drawerRef}
                        id="restaurateur-nav-drawer"
                        role="dialog"
                        aria-modal="true"
                        aria-label="Menu de navigation"
                        tabIndex={-1}
                        className="absolute inset-y-0 left-0 w-[288px] max-w-[85vw] focus:outline-none"
                    >
                        <aside className="h-full bg-cream shadow-xl animate-fade-in">
                            {content}
                        </aside>
                    </div>
                </div>
            )}
        </>
    );
};

export default Sidebar;
